import React from 'react';
import { DisplayGroup, DisplayGroupSettings, GroupInterface, NameDisplayFormat } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';

interface Props {
    group: DisplayGroup;
    onUpdate: (group: DisplayGroup) => void;
}

const Fieldset: React.FC<{ legend: string, children: React.ReactNode, className?: string }> = ({ legend, children, className }) => (
    <div className={`border border-gray-200 dark:border-gray-700 rounded-lg p-3 ${className}`}>
        <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 -mt-5 px-2 bg-white dark:bg-gray-800 w-fit">{legend}</p>
        <div className="pt-2 space-y-3">{children}</div>
    </div>
);

const Checkbox: React.FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
    <label className={`flex items-center gap-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
        <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary"/>
        <span>{label}</span>
    </label>
);

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
    <select {...props} className={`block w-full bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded-md py-1.5 px-2 focus:outline-none focus:ring-brand-secondary focus:border-brand-secondary ${props.className} disabled:opacity-60`}>
        {props.children}
    </select>
);

const DisplayGroupSettingsForm: React.FC<Props> = ({ group, onUpdate }) => {
    const { shifts } = useTimeClock();

    const handleSettingChange = <K extends keyof DisplayGroupSettings>(key: K, value: DisplayGroupSettings[K]) => {
        onUpdate({ ...group, settings: { ...group.settings, [key]: value } });
    };
    const handleNestedChange = (key: keyof DisplayGroupSettings, subKey: string, value: any) => {
        const currentSetting = group.settings[key] as any;
        handleSettingChange(key, { ...currentSetting, [subKey]: value });
    };
    const handleDeepNestedChange = (key: keyof DisplayGroupSettings, subKey: string, deepKey: string, value: any) => {
        const currentSetting = group.settings[key] as any;
        const currentSubSetting = currentSetting[subKey] as any;
        handleNestedChange(key, subKey, { ...currentSubSetting, [deepKey]: value });
    };
    const nameFormat = group.settings.nameFormat || NameDisplayFormat.FIRST_LAST;
    return (
        <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                <div className="space-y-4">
                    <Fieldset legend="Group Interface">
                       <div className="flex items-center gap-2">
                           <Select
                             value={group.settings.groupInterface || GroupInterface.GRID}
                             onChange={e => handleSettingChange('groupInterface', e.target.value as GroupInterface)}
                           >
                            {Object.values(GroupInterface).map(gi => (
                                <option key={gi} value={gi}>{gi}</option>
                            ))}
                           </Select>
                       </div>
                    </Fieldset>
                    <Fieldset legend="Shift">
                        <Select
                            value={group.settings.shiftId || ''}
                            onChange={e => handleSettingChange('shiftId', e.target.value || null)}
                        >
                            <option value="">No Assigned Shift</option>
                            {shifts.map(shift => (
                                <option key={shift.id} value={shift.id}>{shift.name}</option>
                            ))}
                        </Select>
                    </Fieldset>
                    <Fieldset legend="Name Format">
                         <Select
                             value={nameFormat}
                             onChange={e => handleSettingChange('nameFormat', e.target.value as NameDisplayFormat)}
                           >
                            {Object.values(NameDisplayFormat).map(nf => (
                                <option key={nf} value={nf}>{nf}</option>
                            ))}
                           </Select>
                    </Fieldset>
                    {group.settings.groupInterface !== GroupInterface.PIN_ONLY && (
                        <Fieldset legend="Options">
                             <Checkbox 
                                label="Require Employee PIN on Grid / In-Out Board" 
                                checked={group.settings.options.requireEmployeePINs ?? true} 
                                onChange={v => handleNestedChange('options', 'requireEmployeePINs', v)} 
                            />
                        </Fieldset>
                    )}
                     <Fieldset legend="Enable Lunches">
                        <Checkbox label="Show 'Lunch' button" checked={group.settings.breaksAndLunches.showLunchButton} onChange={v => handleNestedChange('breaksAndLunches', 'showLunchButton', v)} />
                    </Fieldset>
                </div>
                <div className="space-y-4">
                     {group.settings.groupInterface === GroupInterface.IN_OUT_BOARD && (
                        <Fieldset legend="In/Out Board Options">
                           <div className="grid grid-cols-3 items-start gap-2">
                               <label>Columns:</label>
                               <div className="col-span-2 space-y-1">
                                    <Checkbox label="Status" checked={group.settings.inOutBoardOptions.columns.status} onChange={v => handleDeepNestedChange('inOutBoardOptions', 'columns', 'status', v)} />
                                    <Checkbox label="Date" checked={group.settings.inOutBoardOptions.columns.date} onChange={v => handleDeepNestedChange('inOutBoardOptions', 'columns', 'date', v)} />
                                    <Checkbox label="Time" checked={group.settings.inOutBoardOptions.columns.time} onChange={v => handleDeepNestedChange('inOutBoardOptions', 'columns', 'time', v)} />
                                    <Checkbox label="Elapsed" checked={group.settings.inOutBoardOptions.columns.elapsed} onChange={v => handleDeepNestedChange('inOutBoardOptions', 'columns', 'elapsed', v)} />
                               </div>
                           </div>
                        </Fieldset>
                     )}
                </div>
            </div>
        </div>
    );
};

export default DisplayGroupSettingsForm;